import { ChevronDownIcon, ChevronRightIcon, DocumentIcon } from "@heroicons/react/20/solid";
import * as Dialog from "@radix-ui/react-dialog";
import { Link } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ThreeDots } from "react-bootstrap-icons";
import { ReactComponent as ExcelIcon } from "assets/img/documents/excel.svg";
import { ReactComponent as ImageIcon } from "assets/img/documents/image.svg";
import { ReactComponent as PdfIcon } from "assets/img/documents/pdf.svg";
import { ReactComponent as WordIcon } from "assets/img/documents/word.svg";
import { MarkdownContent, Table as T, TableSortIcon } from "components/elements";
import { DeleteDialogModal } from "components/elements/DeleteDialogModal";
import { UploadDocument } from "components/form/UploadDocument";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useUserTimezone } from "hooks/useUserTimezone";
import { cn } from "lib/cn";
import { formatISODate } from "lib/formatISODate";
import { humanizeBytes } from "lib/humanizeBytes";
import { trpc } from "lib/trpc";
import { ClientDocument, ClientDocumentListEntry, DocumentsList } from "@compass/shared/types";
import { ReferencesModal } from "./ReferencesModal";

export type DocumentListProps = {
    documents: ClientDocumentListEntry[];
    onDownload?: (id: string, type?: string | null) => void;
    onSelect?: (document: ClientDocument) => void;
    sort?: DocumentsList["sort"];
    sortBy?: DocumentsList["sortBy"];
    toggleSortField?: (field: DocumentsList["sortBy"]) => void;
};

const AIStatusBadge = ({ doc }: { doc: ClientDocumentListEntry }) => {
    if (doc.aiOptOut !== false) {
        return <span className="text-xs text-gray-400">Off</span>;
    }

    const status = doc.aiDescriptionStatus;

    if (status === "DONE") {
        return (
            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                Done
            </span>
        );
    }
    if (status === "PENDING") {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
                <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                    />
                    <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                </svg>
                Processing
            </span>
        );
    }
    if (status === "ERROR") {
        return (
            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                Error
            </span>
        );
    }

    return (
        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
            Queued
        </span>
    );
};

export const AllDocumentsTable = (props: DocumentListProps) => {
    const { documents, onDownload, onSelect, sort, sortBy, toggleSortField } = props;
    const user = useCurrentUser();
    const timeZone = useUserTimezone();
    const { enqueueSnackbar } = useSnackbar();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [editingDocument, setEditingDocument] = useState<ClientDocumentListEntry | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<ClientDocumentListEntry | null>(null);
    const [aiTranscribeTarget, setAiTranscribeTarget] = useState<string | null>(null);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
    const [referenceDocument, setReferenceDocument] = useState<ClientDocumentListEntry | null>(
        null,
    );
    const [visibilityTarget, setVisibilityTarget] = useState<ClientDocumentListEntry | null>(null);

    const toggleExpanded = (id: string) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpenDropdownId(null);
            }
        }

        if (openDropdownId !== null) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [openDropdownId]);

    const Icon = useCallback(
        ({ field }: { field: DocumentsList["sortBy"] }) => {
            return (
                <>
                    <TableSortIcon<DocumentsList> sort={sort} sortBy={sortBy} field={field} />
                </>
            );
        },
        [sort, sortBy],
    );

    const utils = trpc.useContext();

    const deleteDocument = trpc.userContent.delete.useMutation({
        onSuccess: () => {
            utils.userContent.list.invalidate();
            setIsDeleteModalOpen(false);
            setDeleteTarget(null);
        },
    });

    const changeVisibility = trpc.userContent.changeVisibility.useMutation({
        onSuccess: () => {
            utils.userContent.list.invalidate();
            enqueueSnackbar("Visibility changed", { variant: "success" });
            setVisibilityTarget(null);
        },
        onError: (error) => {
            enqueueSnackbar(`Failed to change visibility: ${error.message}`, {
                variant: "error",
            });
            setVisibilityTarget(null);
        },
    });

    const triggerAI = trpc.userContent.triggerAI.useMutation({
        onSuccess: () => {
            utils.userContent.list.invalidate();
            enqueueSnackbar("AI transcription started", { variant: "success" });
        },
        onError: (error) => {
            enqueueSnackbar(`Failed to start AI transcription: ${error.message}`, {
                variant: "error",
            });
        },
    });

    const clearAI = trpc.userContent.clearAI.useMutation({
        onSuccess: () => {
            utils.userContent.list.invalidate();
            enqueueSnackbar("AI transcription cleared", { variant: "success" });
        },
        onError: (error) => {
            enqueueSnackbar(`Failed to clear AI transcription: ${error.message}`, {
                variant: "error",
            });
        },
    });

    const handleDelete = () => {
        if (deleteTarget) {
            deleteDocument.mutate({ fileId: deleteTarget.id });
        }
    };

    const getDocumentIcon = (type?: string | null) => {
        if (!type) return <DocumentIcon className="" />;

        if (type.includes("pdf")) {
            return <PdfIcon className="" />;
        }
        if (type.includes("wordprocessingml")) {
            return <WordIcon className="" />;
        }
        if (type.includes("spreadsheetml")) {
            return <ExcelIcon className="" />;
        }
        if (type.startsWith("image/")) {
            return <ImageIcon className="" />;
        }

        return <DocumentIcon className="h-auto w-[28px]" />;
    };

    const hasAIContent = (doc: ClientDocumentListEntry) =>
        doc.aiDescriptionShort || doc.aiDescriptionLong;

    // Column count for colSpan on the expanded row
    // Name + Uploaded + References + Visibility + AI Status + Owner + (Organizations?) + (Manage?)
    const colCount = 6 + (user?.isCompassUser ? 1 : 0) + (onDownload ? 1 : 0);

    if (documents.length === 0) {
        return null;
    }

    return (
        <>
            <T.Table className="overflow-visible p-3">
                <T.Head>
                    <T.Row className="border-0">
                        <T.IconCell
                            className="bg-brand-atlantic group cursor-pointer border-0 text-white"
                            icon={<Icon field="name" />}
                            onClick={() => toggleSortField?.("name")}
                        >
                            Name
                        </T.IconCell>
                        <T.IconCell
                            className="bg-brand-atlantic group cursor-pointer border-0 text-white"
                            icon={<Icon field="uploaded" />}
                            onClick={() => toggleSortField?.("uploaded")}
                        >
                            Uploaded
                        </T.IconCell>
                        <T.Cell className="bg-brand-atlantic border-0 text-white">
                            References
                        </T.Cell>
                        <T.Cell className="bg-brand-atlantic border-0 text-white">
                            Visibility
                        </T.Cell>
                        <T.Cell className="bg-brand-atlantic border-0 text-white">AI Status</T.Cell>
                        <T.IconCell
                            className="bg-brand-atlantic group cursor-pointer text-white"
                            icon={<Icon field="owner" />}
                            onClick={() => toggleSortField?.("owner")}
                        >
                            Owner
                        </T.IconCell>
                        {user?.isCompassUser && (
                            <T.Cell className="bg-brand-atlantic border-0 text-white">
                                Organizations
                            </T.Cell>
                        )}
                        {onDownload && (
                            <T.Cell className="bg-brand-atlantic border-0 text-white">
                                <div className="text-right">Manage</div>
                            </T.Cell>
                        )}
                    </T.Row>
                </T.Head>
                <T.Body>
                    {documents.map((document) => {
                        const isExpanded = expandedRows.has(document.id);
                        const isChangingVisibility =
                            changeVisibility.isPending &&
                            changeVisibility.variables?.fileId === document.id;
                        const isOwnFile = document.author.id === user?.id;
                        const aiEnabled = document.aiOptOut === false;
                        const aiDone = document.aiDescriptionStatus === "DONE";

                        return (
                            <React.Fragment key={document.id}>
                                <T.Row
                                    className={cn(
                                        "even:bg-brand-polarice odd:bg-[#edf2f7]",
                                        isChangingVisibility && "opacity-50",
                                    )}
                                >
                                    <T.Cell>
                                        <div className="flex items-center gap-2">
                                            {hasAIContent(document) ? (
                                                <button
                                                    onClick={() => toggleExpanded(document.id)}
                                                    className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                                                >
                                                    {isExpanded ? (
                                                        <ChevronDownIcon className="h-4 w-4" />
                                                    ) : (
                                                        <ChevronRightIcon className="h-4 w-4" />
                                                    )}
                                                </button>
                                            ) : (
                                                <div className="w-5 shrink-0" />
                                            )}
                                            {getDocumentIcon(document.type)}
                                            <div className="flex flex-row items-baseline gap-2">
                                                <div
                                                    className={cn(
                                                        "flex-col font-medium text-[#424242]",
                                                    )}
                                                >
                                                    <div
                                                        onClick={() => onDownload?.(document.id)}
                                                        className="cursor-pointer hover:underline"
                                                    >
                                                        {document.displayName || document.name}
                                                    </div>
                                                    {(document?.references ?? 0) > 0 && (
                                                        <div
                                                            onClick={() =>
                                                                setReferenceDocument(document)
                                                            }
                                                            className="text-brand-alice hover:text-brand-azure cursor-pointer text-sm hover:underline"
                                                        >
                                                            See References
                                                        </div>
                                                    )}
                                                </div>
                                                {document.sizeBytes && (
                                                    <div className="text-brand-alice text-sm">
                                                        {humanizeBytes(document.sizeBytes)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </T.Cell>
                                    <T.Cell>
                                        <div className="text-black">
                                            {formatISODate(document.uploaded, timeZone)}
                                        </div>
                                    </T.Cell>
                                    <T.Cell>
                                        <div className="cursor-pointer px-3 text-black">
                                            {document.references}
                                        </div>
                                    </T.Cell>
                                    <T.Cell>
                                        {isChangingVisibility ? (
                                            <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
                                                <svg
                                                    className="h-3 w-3 animate-spin"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    />
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                    />
                                                </svg>
                                                Moving...
                                            </span>
                                        ) : (
                                            <span
                                                className={cn(
                                                    "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                                                    document.context === "PUBLIC_ATTACHMENT"
                                                        ? "bg-green-100 text-green-700"
                                                        : "bg-gray-100 text-gray-600",
                                                )}
                                            >
                                                {document.context === "PUBLIC_ATTACHMENT"
                                                    ? "Public"
                                                    : "Private"}
                                            </span>
                                        )}
                                    </T.Cell>
                                    <T.Cell>
                                        <AIStatusBadge doc={document} />
                                    </T.Cell>
                                    <T.Cell>
                                        <div className="text-black">
                                            {document?.author?.isSocialEnabled ? (
                                                <Link
                                                    target="_blank"
                                                    to="/social/profile/$id"
                                                    params={{
                                                        id: document.author?.id?.toString() || "",
                                                    }}
                                                >
                                                    {document.author.name}
                                                    {!document?.author?.isPrivateEnabled &&
                                                        " (private inactive)"}
                                                </Link>
                                            ) : (
                                                <span className="italic text-black">
                                                    {document.author.name}
                                                    {!document?.author?.isPrivateEnabled
                                                        ? " (inactive)"
                                                        : " (social inactive)"}
                                                </span>
                                            )}
                                        </div>
                                    </T.Cell>
                                    {user?.isCompassUser && (
                                        <T.Cell>
                                            <div className="text-black">
                                                {document.organizations
                                                    ? document.organizations.join(", ")
                                                    : " - "}
                                            </div>
                                        </T.Cell>
                                    )}

                                    {onDownload && (
                                        <T.Cell>
                                            <div className="relative flex justify-end">
                                                <ThreeDots
                                                    className="h-5 w-5 cursor-pointer text-gray-500"
                                                    onClick={() =>
                                                        setOpenDropdownId(
                                                            openDropdownId === document.id
                                                                ? null
                                                                : document.id,
                                                        )
                                                    }
                                                />
                                                {openDropdownId === document.id && (
                                                    <div
                                                        ref={dropdownRef}
                                                        className="absolute right-0 z-10 mt-2 w-48 rounded-md border border-gray-200 bg-white shadow-sm"
                                                    >
                                                        {isOwnFile && (
                                                            <button
                                                                onClick={() => {
                                                                    setEditingDocument(document);
                                                                    setIsEditModalOpen(true);
                                                                    setOpenDropdownId(null);
                                                                }}
                                                                className="block w-full px-4 py-1 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                            >
                                                                Update
                                                            </button>
                                                        )}

                                                        <button
                                                            onClick={() => {
                                                                onDownload(
                                                                    document.id,
                                                                    document.type,
                                                                );
                                                                setOpenDropdownId(null);
                                                            }}
                                                            className="block w-full px-4 py-1 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                        >
                                                            Download
                                                        </button>

                                                        {isOwnFile && (
                                                            <button
                                                                onClick={() => {
                                                                    setVisibilityTarget(document);
                                                                    setOpenDropdownId(null);
                                                                }}
                                                                disabled={
                                                                    changeVisibility.isPending
                                                                }
                                                                className="block w-full px-4 py-1 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                            >
                                                                {document.context ===
                                                                "PUBLIC_ATTACHMENT"
                                                                    ? "Make Private"
                                                                    : "Make Public"}
                                                            </button>
                                                        )}

                                                        {/* AI Transcription actions */}
                                                        {isOwnFile && (
                                                            <>
                                                                <div className="border-t border-gray-200" />
                                                                {!aiEnabled ? (
                                                                    <button
                                                                        onClick={() => {
                                                                            setAiTranscribeTarget(
                                                                                document.id,
                                                                            );
                                                                            setOpenDropdownId(null);
                                                                        }}
                                                                        className="block w-full px-4 py-1 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                                    >
                                                                        AI Transcribe
                                                                    </button>
                                                                ) : (
                                                                    <>
                                                                        <button
                                                                            onClick={() => {
                                                                                setAiTranscribeTarget(
                                                                                    document.id,
                                                                                );
                                                                                setOpenDropdownId(
                                                                                    null,
                                                                                );
                                                                            }}
                                                                            className="block w-full px-4 py-1 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                                        >
                                                                            {aiDone
                                                                                ? "Regenerate Transcription"
                                                                                : "AI Transcribe"}
                                                                        </button>
                                                                        <button
                                                                            onClick={() => {
                                                                                clearAI.mutate({
                                                                                    fileId: document.id,
                                                                                });
                                                                                setOpenDropdownId(
                                                                                    null,
                                                                                );
                                                                            }}
                                                                            disabled={
                                                                                clearAI.isPending
                                                                            }
                                                                            className="block w-full px-4 py-1 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                                        >
                                                                            Clear Transcription
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </>
                                                        )}

                                                        {isOwnFile && (
                                                            <>
                                                                <div className="border-t border-gray-200" />
                                                                <button
                                                                    onClick={() => {
                                                                        setDeleteTarget(document);
                                                                        setIsDeleteModalOpen(true);
                                                                        setOpenDropdownId(null);
                                                                    }}
                                                                    className="block w-full px-4 py-1 text-left text-sm text-red-500 hover:bg-gray-100"
                                                                >
                                                                    Delete
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </T.Cell>
                                    )}
                                </T.Row>

                                {/* Expandable AI content row */}
                                {isExpanded && hasAIContent(document) && (
                                    <tr className="bg-slate-50">
                                        <td colSpan={colCount} className="px-6 py-3">
                                            <div className="flex flex-col gap-3 text-xs">
                                                {document.aiDescriptionShort && (
                                                    <div>
                                                        <div className="font-semibold uppercase tracking-wide text-gray-400">
                                                            AI Summary
                                                        </div>
                                                        <div className="mt-1">
                                                            <MarkdownContent
                                                                markdown={
                                                                    document.aiDescriptionShort
                                                                }
                                                                expanded
                                                                className="prose-xs text-xs"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                {document.aiDescriptionLong && (
                                                    <div>
                                                        <div className="font-semibold uppercase tracking-wide text-gray-400">
                                                            AI Description
                                                        </div>
                                                        <div className="mt-1">
                                                            <MarkdownContent
                                                                markdown={
                                                                    document.aiDescriptionLong
                                                                }
                                                                expanded
                                                                className="prose-xs text-xs"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </T.Body>
            </T.Table>

            {referenceDocument && referenceDocument.discussionIds?.length > 0 && (
                <ReferencesModal
                    isOpen={!!referenceDocument}
                    setIsOpen={(open) => {
                        if (!open) setReferenceDocument(null);
                    }}
                    discussionIds={referenceDocument.discussionIds}
                />
            )}

            {editingDocument && (
                <UploadDocument
                    isOpen={isEditModalOpen}
                    setIsOpen={(open) => {
                        setIsEditModalOpen(open);
                        if (!open) setEditingDocument(null);
                    }}
                    button={null}
                    title="Edit Document"
                    mode="edit"
                    initialValues={{
                        id: editingDocument.id,
                        filename: (editingDocument.displayName || editingDocument.name) ?? "",
                        description: editingDocument.description ?? "",
                    }}
                    onUpload={() => undefined}
                />
            )}

            {deleteTarget && (
                <DeleteDialogModal
                    open={isDeleteModalOpen}
                    setOpen={(isOpen) => {
                        setIsDeleteModalOpen(isOpen);
                        if (!isOpen) setDeleteTarget(null);
                    }}
                    handleDelete={handleDelete}
                    disabled={deleteDocument.isPending}
                    message="This action cannot be undone. This will permanently delete your document."
                />
            )}

            <Dialog.Root
                open={!!visibilityTarget}
                onOpenChange={(open) => {
                    if (!open && !changeVisibility.isPending) setVisibilityTarget(null);
                }}
            >
                <Dialog.Portal>
                    <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
                    <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl shadow-xl">
                        <div className="space-y-4 bg-white p-5">
                            <Dialog.Title className="font-sans text-lg font-semibold text-[#424242]">
                                {visibilityTarget?.context === "PUBLIC_ATTACHMENT"
                                    ? "Make Document Private"
                                    : "Make Document Public"}
                            </Dialog.Title>
                            <Dialog.Description className="text-sm text-gray-600">
                                {visibilityTarget?.context === "PUBLIC_ATTACHMENT"
                                    ? "This document will no longer be visible to other users. Are you sure you want to make it private?"
                                    : "This document will become visible to all users in your organization. Are you sure you want to make it public?"}
                            </Dialog.Description>
                            <div className="flex justify-start gap-2">
                                <button
                                    type="button"
                                    className="rounded bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                                    disabled={changeVisibility.isPending}
                                    onClick={() => {
                                        if (!visibilityTarget) return;
                                        changeVisibility.mutate({
                                            fileId: visibilityTarget.id,
                                            context:
                                                visibilityTarget.context === "PUBLIC_ATTACHMENT"
                                                    ? "PRIVATE_ATTACHMENT"
                                                    : "PUBLIC_ATTACHMENT",
                                        });
                                    }}
                                >
                                    {changeVisibility.isPending ? "Saving..." : "Confirm"}
                                </button>
                                <Dialog.Close asChild>
                                    <button
                                        type="button"
                                        className="rounded bg-gray-100 px-4 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-200 disabled:opacity-50"
                                        disabled={changeVisibility.isPending}
                                    >
                                        Cancel
                                    </button>
                                </Dialog.Close>
                            </div>
                        </div>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>

            {/* AI Transcribe confirmation dialog */}
            <Dialog.Root
                open={!!aiTranscribeTarget}
                onOpenChange={(open) => {
                    if (!open) setAiTranscribeTarget(null);
                }}
            >
                <Dialog.Portal>
                    <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
                    <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-lg shadow-lg">
                        <div className="space-y-4 bg-white p-5">
                            <Dialog.Title className="text-lg font-semibold text-gray-800">
                                Enable AI Transcription
                            </Dialog.Title>
                            <Dialog.Description className="text-sm text-gray-600">
                                This file will be sent to a third-party AI service for analysis.
                                Please do not submit files containing sensitive, confidential, or
                                personally identifiable information (PII).
                            </Dialog.Description>
                            <div className="flex justify-start gap-2">
                                <button
                                    type="button"
                                    className="rounded bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                                    disabled={triggerAI.isPending}
                                    onClick={() => {
                                        if (aiTranscribeTarget) {
                                            triggerAI.mutate({ fileId: aiTranscribeTarget });
                                        }
                                        setAiTranscribeTarget(null);
                                    }}
                                >
                                    Proceed
                                </button>
                                <Dialog.Close asChild>
                                    <button className="rounded bg-gray-100 px-4 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-200">
                                        Cancel
                                    </button>
                                </Dialog.Close>
                            </div>
                        </div>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
        </>
    );
};
